// cspell:words Essenced
import classNames from "classnames";
import styled from "styled-components";
import { FC, ReactNode, ReactElement } from "react";

// Utils
import { getBundleCartLimit } from "../../utils/bundle";
import { getProductForContentfulId } from "../../utils/planToProduct";
import { Color, Opacity, rem, responsive, rgba } from "../../utils/style";

// Redux
import { useSelector, useDispatch } from "react-redux";
import {
  removeProductFromCart,
  updateCartProductQuantity,
} from "../../store/cart/actions";
import planSelectors from "../../store/plan/selectors";
import productSelectors from "../../store/product/selectors";

// Store Models
import CartProduct from "../../store/cart-product/model";
import Plan from "../../store/plan/model";
import Product from "../../store/product/model";
import ProductOffer from "../../store/product-offer/model";

// Components
import { GatsbyImage, getImage, IGatsbyImageData } from "gatsby-plugin-image";
import { getStore } from "../../store/createStore";
import { productOfferForId } from "../../store/product-offer/selectors";
import currencyString from "../Currency";
import MagicLink from "../MagicLink";
import StrikeText from "../global/StrikeText";
import QuantityStepper from "./QuantityStepper";
import { variation } from "../../services/launchDarkly";
import { ContentfulProduct, ContentfulImage } from "../../utils/productTypes";

export interface CartItemProps {
  cartProduct: CartProduct;
  contentfulProduct?: ContentfulProduct;
  cartQuantity: number;
  className?: string;
  isFlyoutContext?: boolean;
  disableQuantitySelector?: boolean;
}

// Styled Elements
export const CartItemContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${Color.white};
  padding: 16px;

  &::before {
    content: "";
    position: absolute;
    right: 16px;
    bottom: 0;
    left: 16px;
    border-top: 1px solid ${Color.fadedGrey};
  }

  &.simplified {
    padding: 16px 0;

    &::before {
      right: 0;
      bottom: 0;
      left: 0;
      border-top: 1px solid ${Color.veryFadedGrey};
    }

    &.last-item {
      &::before {
        border: none !important;
      }
    }
  }

  ${responsive.md`
    padding: 24px;

    &::before {
      right: 24px;
      left: 24px;
    }
  `};

  &:last-child {
    &:not(.simplified) {
      border-bottom: none;
      margin-bottom: 0;

      &::before {
        display: none;
      }
    }
  }
`;

export const SectionWrapper = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  margin-top: 0;
  margin-right: var(--spacing-0_5);
`;

export const ImageAndSectionContainer = styled.div`
  display: flex;
`;

// Remove a11y focus from image link since title link is immediately adjacent (duplicate)
const ImageWrapper = styled(MagicLink).attrs({
  tabIndex: -1,
  "aria-hidden": "true",
})`
  position: relative;
  width: 97px;
  height: 105px;
  margin-right: var(--spacing-1);
  flex: 0 0 97px;
`;

const ImageContainer = styled.div`
  position: relative;
  width: 97px;
  height: 105px;
  margin-right: var(--spacing-1);
  flex: 0 0 97px;
  overflow: hidden;
`;

export const SubscriptionInfo = styled.p`
  color: ${rgba(Color.ritualBlue, Opacity.light)};
  font-size: 12px;
  font-weight: 300;
  letter-spacing: 0px;
  line-height: 22px;
  text-wrap: nowrap;
  display: inline-flex;
  justify-content: space-between;

  ${responsive.md`
    font-size: 14px;
    line-height: 24px;
    display: inline-flex;
    justify-content: space-between;
  `}
  .savings {
    color: var(--Solid-Semantic-SuccessGreen, #4c840d);

    @media (max-width: 375px) {
      display: block;
      margin-left: 0;
    }
  }
`;

export const ProductTitleDefault = styled.p`
  font-size: ${rem(14)};
  line-height: ${rem(24)};
  color: var(--indigo-blue);
  font-weight: 500;
  margin: 0;
  padding-bottom: 8px !important;

  ${responsive.md`
    font-size: ${rem(16)};
    line-height: ${rem(26)};

    padding-bottom: 16px !important;
  `}

  &.product-offer-title {
    font-family: var(--font-dutch);
    font-size: ${rem(12)};
    line-height: ${rem(18)};

    em {
      font-family: var(--font-circular);
      font-style: normal;
      font-size: ${rem(16)};
      line-height: ${rem(24)};
    }
  }
`;

export const ProductTitle = styled.p`
  color: var(--indigo-blue);
  margin-bottom: var(--spacing-0_5) !important;

  span {
    display: block;
  }

  .typography-label4 {
    font-style: italic;
    color: var(--indigo-blue-60);
  }
`;

export const CartItemSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin: 0;
  padding: 0;

  p {
    margin: 0;
    padding: 0;
  }
`;

export const CartItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin: 0;
  margin-top: auto;
  padding-right: var(--spacing-0_5);
  padding: 0;

  p {
    margin: 0;
    padding: 0;
    font-size: ${rem(12)};
    line-height: ${rem(20)};
  }
`;

export const ItemActions = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0;
  margin-left: auto;
  padding: 0;
  align-items: flex-end;

  p {
    margin: 0;
    padding: 0;
  }
`;

const QuantityWrapper = styled.div`
  margin-top: auto;
  margin-left: var(--spacing-0_5);

  span.static-qty {
    text-wrap: nowrap;
  }
`;

const PriceContainer = styled.div`
  color: var(--indigo-blue);
`;

const getProductUrl = (
  contentfulProduct?: ContentfulProduct,
): string | null => {
  if (!contentfulProduct) return null;

  const { contentfulId } = contentfulProduct;
  const { urlSlug } = getProductForContentfulId(contentfulId);

  return `/products/${urlSlug}`;
};

interface ItemTitleProps {
  contentfulProduct?: ContentfulProduct;
  productOffer?: ProductOffer;
  product?: Product;
}

const ItemTitle: FC<ItemTitleProps> = ({
  contentfulProduct,
  productOffer,
  product,
}) => {
  if (!contentfulProduct && !productOffer) {
    return (
      <>
        <span className="typography-label3">{product?.name}</span>
        <span className="typography-label4 font-dutch"></span>
      </>
    );
  }

  const label3Text = productOffer?.name || contentfulProduct?.summary;
  const label4Text = productOffer
    ? productOffer.contentfulProductBundle(getStore().getState())
        .shopDescription
    : contentfulProduct?.productSubhead;

  return (
    <>
      <span className="typography-label3">{label3Text}</span>
      <span className="typography-label4 font-dutch">{label4Text}</span>
    </>
  );
};

const getCartImage = (
  contentfulProduct?: ContentfulProduct,
  productOffer?: ProductOffer,
): ContentfulImage | null => {
  if (!contentfulProduct && !productOffer) return null;

  const state = getStore().getState();

  if (productOffer) {
    return productOffer.contentfulProductBundle(state).shopImages?.[0] || null;
  }

  const { cartImage } = contentfulProduct!;
  return cartImage || null;
};

const priceDifference = (currentPrice: number, futurePrice: number): number => {
  return (futurePrice * 100 - currentPrice * 100) / 100;
};

const getPrice = ({
  cartProduct,
  productOffer,
  plan,
  contentfulProduct,
}: {
  cartProduct: CartProduct;
  productOffer?: ProductOffer;
  plan?: Plan;
  contentfulProduct?: ContentfulProduct;
}): ReactNode => {
  const { productPrice, productSubtotal, subtotal, quantity } = cartProduct;
  const servingsPerContainer =
    contentfulProduct?.supplementFacts?.servingsPerContainer;
  const unitQuantity = plan?.unitQuantity;
  const round = true;

  const showServings = Boolean(unitQuantity) && Boolean(servingsPerContainer);

  const servings =
    showServings && parseInt(servingsPerContainer!) * unitQuantity!;

  if (productOffer) {
    const price = (productPrice / 100).toFixed(2);
    const futurePrice = productOffer.futureAmount(getStore().getState());
    const futurePriceAdjusted = (futurePrice / 100).toFixed(2);

    const savings = priceDifference(
      parseFloat(price),
      parseFloat(futurePriceAdjusted),
    );
    return (
      <>
        <PriceContainer className="price">
          {showServings && (
            <p className="typography-caption">{servings} servings</p>
          )}
          <StrikeText
            contentStyle={{ color: "var(--indigo-blue-60)", opacity: 1 }}
          >
            {currencyString({ value: futurePriceAdjusted, round })}
          </StrikeText>
          {currencyString({ value: price, round })}
          <br />
          <span className="savings">
            {currencyString({ value: savings, round: true })} savings
          </span>
        </PriceContainer>
      </>
    );
  } else if (productSubtotal !== subtotal) {
    let fullPrice = (productSubtotal / 100).toFixed(2);
    const currentPrice = (subtotal / 100).toFixed(2);
    let savings = priceDifference(
      parseFloat(currentPrice),
      parseFloat(fullPrice),
    );

    const welcomeOfferPromotionFlag = variation("welcome-offer-promotion", {});
    const welcomeOfferPromotionEnabled =
      welcomeOfferPromotionFlag?.slug &&
      welcomeOfferPromotionFlag?.slug === "welcome-offer-alc";

    if (welcomeOfferPromotionEnabled && plan) {
      fullPrice = (
        (plan.basePrice * plan.unitQuantity * quantity) /
        100
      ).toFixed(2);
      savings = priceDifference(
        parseFloat(currentPrice),
        parseFloat(fullPrice),
      );
    }

    return (
      <>
        <PriceContainer className="price">
          {showServings && (
            <p className="typography-caption">{servings} servings</p>
          )}
          {plan?.variantDescription && (
            <p className="typography-caption">
              {plan.variantDescription.replace("Essenced", "")}
            </p>
          )}
          <StrikeText
            contentStyle={{ color: "var(--indigo-blue-60)", opacity: 1 }}
          >
            {currencyString({ value: fullPrice, round })}
          </StrikeText>
          {currencyString({ value: currentPrice, round })}
          <br />
          <span className="savings">
            {currencyString({ value: savings, round: true })} savings
          </span>
        </PriceContainer>
      </>
    );
  }

  return (
    <PriceContainer className="price">
      {showServings && (
        <p className="typography-caption">{servings} servings</p>
      )}
      {plan?.variantDescription && (
        <p className="typography-caption">
          {plan.variantDescription.replace("Essenced", "")}
        </p>
      )}
      {currencyString({ value: (productSubtotal / 100).toFixed(2), round })}
    </PriceContainer>
  );
};

const CartItemComponent: FC<CartItemProps> = ({
  cartProduct,
  contentfulProduct,
  cartQuantity,
  className,
  isFlyoutContext,
  disableQuantitySelector,
}) => {
  const dispatch = useDispatch();

  const plan = useSelector((state: any) =>
    planSelectors.planForId(state, { id: cartProduct.planId }),
  );

  const productOffer = useSelector((state: any) =>
    productOfferForId(state, cartProduct.productOfferId),
  );

  const product = useSelector((state: any) =>
    productSelectors.productForId(state, { id: plan?.productId }),
  );

  const renderImage = (
    cartImage: ContentfulImage,
    productUrl: string | null,
    classNames: string,
  ): ReactElement => {
    return (
      <ImageWrapper className={classNames} to={productUrl || ""}>
        <GatsbyImage
          image={getImage(cartImage as unknown as IGatsbyImageData)!}
          loading="eager"
          alt={cartImage.description}
          aria-label={cartImage.description}
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            width: "100%",
            height: "100%",
            zIndex: "0",
            userSelect: "none",
            pointerEvents: "none",
          }}
        />
      </ImageWrapper>
    );
  };

  const handleQuantityChange = (newQuantity: number): void => {
    dispatch(updateCartProductQuantity(cartProduct.planId, newQuantity));
  };

  const removeItem = (cartProduct: CartProduct): void => {
    const productData = {
      planId: cartProduct.planId,
      productOfferId: cartProduct.productOfferId,
    };

    dispatch(removeProductFromCart(productData, true));
  };

  if (!plan && !productOffer) return null;

  const cartImage = getCartImage(contentfulProduct, productOffer);

  const classNameString = classNames(
    className,
    "simplified",
    isFlyoutContext && "flyout",
  );

  const productUrl = productOffer
    ? `/bundles/${productOffer?.slug}`
    : getProductUrl(contentfulProduct);

  const cartLimit = getBundleCartLimit();

  return (
    <>
      <CartItemContainer className={classNameString}>
        <ImageAndSectionContainer>
          {cartImage ? (
            renderImage(cartImage, productUrl, classNameString)
          ) : (
            <ImageContainer>
              {product && (
                <img
                  src={product.imageUrl}
                  alt={product.name}
                  style={{
                    position: "absolute",
                    left: "50%",
                    transform: "translateX(-50%)",
                    top: 0,
                    height: "100%",
                    zIndex: "0",
                    userSelect: "none",
                    pointerEvents: "none",
                  }}
                />
              )}
            </ImageContainer>
          )}
          <SectionWrapper className={classNameString}>
            <CartItemSection>
              <MagicLink className={classNameString} to={productUrl || ""}>
                <ProductTitle>
                  <ItemTitle
                    contentfulProduct={contentfulProduct}
                    productOffer={productOffer}
                    product={product}
                  />
                </ProductTitle>
              </MagicLink>
            </CartItemSection>
            <CartItemInfo className={classNameString}>
              <SubscriptionInfo>
                {getPrice({
                  cartProduct,
                  productOffer,
                  plan,
                  contentfulProduct,
                })}
              </SubscriptionInfo>
            </CartItemInfo>
          </SectionWrapper>
          {disableQuantitySelector ? (
            <ItemActions>
              <QuantityWrapper>
                <span className="static-qty typography-caption">
                  Qty {cartProduct.quantity}
                </span>
              </QuantityWrapper>
            </ItemActions>
          ) : (
            <ItemActions>
              {plan ? (
                <QuantityWrapper>
                  <QuantityStepper
                    quantity={cartProduct.quantity}
                    disableAdd={cartQuantity >= cartLimit}
                    removeAction={() => removeItem(cartProduct)}
                    increaseAction={() =>
                      handleQuantityChange(cartProduct.quantity + 1)
                    }
                    decreaseAction={() =>
                      handleQuantityChange(cartProduct.quantity - 1)
                    }
                    productName={contentfulProduct?.summary}
                  />
                </QuantityWrapper>
              ) : (
                <QuantityWrapper>
                  <QuantityStepper
                    quantity={1}
                    disableAdd={cartQuantity >= cartLimit}
                    removeOnly={true}
                    removeAction={() => removeItem(cartProduct)}
                    increaseAction={() =>
                      handleQuantityChange(cartProduct.quantity + 1)
                    }
                    decreaseAction={() =>
                      handleQuantityChange(cartProduct.quantity - 1)
                    }
                    productName={productOffer?.name}
                  />
                </QuantityWrapper>
              )}
            </ItemActions>
          )}
        </ImageAndSectionContainer>
      </CartItemContainer>
    </>
  );
};

export default CartItemComponent;
